import { z } from 'zod';
import { baseDocSchema, objectIdSchema } from './common.js';

export const qrStatusSchema = z.enum(['issued', 'validated', 'voided', 'refunded']);
export type QrStatus = z.infer<typeof qrStatusSchema>;

export const qrCodeSchema = baseDocSchema.extend({
  orderId: objectIdSchema,
  ticketTypeId: objectIdSchema,
  eventId: objectIdSchema,
  orgId: objectIdSchema,
  // Secret nunca se devuelve al cliente. 32 bytes base64.
  secret: z.string().min(32),
  status: qrStatusSchema.default('issued'),
  validatedAt: z.coerce.date().nullable().default(null),
  validatedByDeviceId: z.string().nullable().default(null),
  validatedByUserId: objectIdSchema.nullable().default(null),
  deviceBoundTo: z.string().nullable().default(null),
  watermarkBuyerHash: z.string().nullable().default(null),
  validityFrom: z.coerce.date().optional(),
  validityTo: z.coerce.date().optional(),
  ticketSnapshot: z.object({ name: z.string(), kind: z.string() }),
  /**
   * Total de intentos de escaneo (incluye el primero válido y todos los
   * duplicados subsiguientes). Sirve para que el staff sepa cuántas veces
   * un mismo boleto se intentó usar — un valor alto en un QR genuino
   * suele indicar intento de reventa.
   */
  scanCount: z.number().int().nonnegative().default(0),
  /** Timestamp del último intento de scan (válido o duplicado). */
  lastScannedAt: z.coerce.date().nullable().default(null),
  /**
   * EventTicketObject ID de Google Wallet (`<issuerId>.ticket-<qrId>`).
   * `null` si Wallet está deshabilitado o si su creación falló y todavía
   * no se reintentó vía el endpoint público.
   */
  googleWalletObjectId: z.string().nullable().default(null),
});
export type QrCode = z.infer<typeof qrCodeSchema>;

/** Payload safe-for-client (sin `secret`). */
export const qrCodePublicSchema = qrCodeSchema.omit({ secret: true });
export type QrCodePublic = z.infer<typeof qrCodePublicSchema>;

export const validateQrInputSchema = z.object({
  payload: z.string().min(1),
  deviceId: z.string().min(1),
  scannedAt: z.coerce.date().default(() => new Date()),
});
export type ValidateQrInput = z.infer<typeof validateQrInputSchema>;

export const validateQrResultSchema = z.discriminatedUnion('result', [
  z.object({
    result: z.literal('valid'),
    qrId: objectIdSchema,
    ticketSnapshot: z.object({ name: z.string(), kind: z.string() }),
    /** Cuántas veces el QR ha sido escaneado en total (1 = primer scan). */
    scanCount: z.number().int().positive(),
    validatedAt: z.coerce.date(),
  }),
  z.object({
    result: z.literal('duplicate'),
    qrId: objectIdSchema,
    ticketSnapshot: z.object({ name: z.string(), kind: z.string() }),
    /** Cuándo se validó por primera vez. */
    validatedAt: z.coerce.date(),
    validatedByDeviceId: z.string().nullable(),
    /** Total de intentos (incluye el válido inicial + todos los duplicados). */
    scanCount: z.number().int().positive(),
    buyerPhotoUrl: z.string().url().optional(),
  }),
  z.object({
    result: z.literal('invalid'),
    reason: z.enum(['wrong_event', 'tampered', 'voided', 'malformed', 'unknown']),
  }),
]);
export type ValidateQrResult = z.infer<typeof validateQrResultSchema>;
